import React, {useEffect, useState} from 'react'
import {
  Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Drawer, Link, Stack, TextField, Typography,
} from '@mui/material'
import {Transaction} from '../../domain/Transaction'
import {useIsMobile} from '../../utils/useIsMobile'
import {formatBalanceWithTwoDecimals} from '../../utils/formatValuesUtils'

type RecurringOccurrenceEditorProps = {
  open: boolean,
  transaction: Transaction,
  onClose: () => void,
  onApply: (newAmount: number, justification: string) => void,
}

export const RecurringOccurrenceEditor: React.FC<RecurringOccurrenceEditorProps> = ({open, transaction, onClose, onApply}) => {
  const isMobile = useIsMobile()
  const [amount, setAmount] = useState<string>(String(transaction.amount))
  const [justification, setJustification] = useState('')
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    setAmount(String(transaction.amount))
    setJustification('')
    setShowHistory(false)
  }, [transaction, open])

  const modifications = transaction.amountModifications ?? []
  const initialAmount = modifications.length > 0 ? modifications[0].previousAmount : transaction.amount
  const newAmount = parseFloat(amount) || 0
  const amountChanged = newAmount !== transaction.amount
  const canValidate = !amountChanged || justification.trim().length > 0

  const handleValidate = () => {
    if (amountChanged) {
      onApply(newAmount, justification.trim())
    }
    onClose()
  }

  const fields = (
    <Stack spacing={2} sx={{pt: 1}}>
      <TextField
        label="Montant" type="number" value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputProps={{inputMode: 'decimal'}} fullWidth
      />
      {amountChanged && (
        <TextField
          label="Justification" value={justification}
          onChange={(e) => setJustification(e.target.value)}
          fullWidth multiline
        />
      )}
      <Box>
        <Link component="button" type="button" onClick={() => setShowHistory(v => !v)} data-testid="toggle-history">
          Voir l'historique ({modifications.length})
        </Link>
        <Collapse in={showHistory}>
          <Box sx={{mt: 1}}>
            <Typography variant="body2">Montant initial : {formatBalanceWithTwoDecimals(initialAmount)} €</Typography>
            {modifications.map((m, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {m.timestamp.slice(0, 10)} · {formatBalanceWithTwoDecimals(m.previousAmount)} → {formatBalanceWithTwoDecimals(m.newAmount)} € — « {m.justification} »
              </Typography>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Stack>
  )

  const actions = (
    <>
      <Button onClick={onClose}>Annuler</Button>
      <Button variant="contained" onClick={handleValidate} disabled={!canValidate}>Valider</Button>
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom" open={open} onClose={onClose}
        PaperProps={{sx: {borderTopLeftRadius: 12, borderTopRightRadius: 12, width: '100vw', maxWidth: '100vw', boxSizing: 'border-box', overflow: 'hidden'}}}
      >
        <Box sx={{p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))', maxHeight: '85dvh', overflowY: 'auto', boxSizing: 'border-box'}}>
          <Typography variant="h6">{transaction.description}</Typography>
          {fields}
          <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>{actions}</Box>
        </Box>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{transaction.description}</DialogTitle>
      <DialogContent>{fields}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  )
}
